import { Link, useNavigate, useLocation } from "react-router-dom";
import { Settings, LogOut, Users, ArrowLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useSidebar } from "@/contexts/SidebarContext";
import { safeLocalStorage } from "@/lib/safeStorage";
import { useAuth } from "@/contexts/AuthContext";
import { coProducerHomePath } from "@/lib/coProducerAccess";

interface AppSidebarProps {
  /** Where the top-left back control navigates. Defaults to the dashboard. */
  backTo?: string;
  /** Label/title for the back control. Defaults to "Back to Dashboard". */
  backLabel?: string;
}

const AppSidebar = ({
  backTo = "/dashboard",
  backLabel = "Back to Dashboard",
}: AppSidebarProps = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { expanded, setExpanded } = useSidebar();
  const { isCoProducerOnly, coProducerBriefingIds } = useAuth();

  // Check if we're on the dashboard
  const isOnDashboard = location.pathname === "/dashboard";

  // A co-producer has no dashboard/settings/my-team layer: the back control
  // returns to their production overview instead of the (forbidden) dashboard.
  const coProducerHome = coProducerHomePath(coProducerBriefingIds);
  const effectiveBackTo = isCoProducerOnly && coProducerHome ? coProducerHome : backTo;
  const effectiveBackLabel = isCoProducerOnly && coProducerHome ? "Back to production" : backLabel;

  const handleLogout = async () => {
    try {
      // Stop all queries first to prevent CORS errors
      queryClient.cancelQueries();
      queryClient.clear();

      // Then sign out
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      toast({
        title: "Success",
        description: "You have been logged out successfully.",
      });
      navigate("/producer-login", { replace: true });
    } catch (error) {
      console.error(error);
      // Even if error, force logout
      safeLocalStorage.clear();
      toast({
        title: "Logged out",
        description: "You have been logged out.",
      });
      navigate("/producer-login", { replace: true });
    }
  };

  const handleBackClick = (e: React.MouseEvent) => {
    if (!isOnDashboard) {
      e.preventDefault();
      navigate(effectiveBackTo);
    }
    // If on dashboard, allow default behavior (navigate to homepage)
  };

  return (
    <motion.aside
      className="fixed z-50 flex h-screen flex-col overflow-hidden border-r border-border bg-background py-6"
      animate={{ width: expanded ? 220 : 72 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      style={{ pointerEvents: "auto" }}
    >
      {/* Logo */}
      <div className="mb-8 px-3">
        <a
          href={isOnDashboard ? "/" : effectiveBackTo}
          onClick={handleBackClick}
          className="group flex h-12 items-center transition-all"
          title={isOnDashboard ? "Back to PROduce" : effectiveBackLabel}
        >
          <div className="relative flex w-12 flex-shrink-0 items-center justify-center">
            <img
              src="/produce-favicon.png"
              alt="PROduce"
              className="absolute left-1/2 top-1/2 h-8 w-8 -translate-x-1/2 -translate-y-1/2 object-contain transition-opacity group-hover:opacity-0"
            />
            <ArrowLeft className="absolute left-1/2 top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 text-primary opacity-0 transition-opacity group-hover:opacity-100" />
          </div>
          {expanded && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="ml-2 whitespace-nowrap text-sm"
            >
              {isOnDashboard ? "Back to PROduce" : effectiveBackLabel}
            </motion.span>
          )}
        </a>
      </div>

      <div className="flex-1" />

      {/* Bottom Actions — My Team / Settings are the "layer above" a single
          production, so they're hidden for co-producers. */}
      <div className="flex flex-col gap-2 px-3">
        {!isCoProducerOnly && (
          <Link
            to="/my-team"
            data-guide="my-team"
            className="flex h-12 items-center rounded-lg text-muted-foreground transition-colors hover:bg-muted/10 hover:text-foreground"
            aria-label="My Team"
          >
            <div className="flex w-12 flex-shrink-0 items-center justify-center">
              <Users className="h-5 w-5" />
            </div>
            {expanded && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="whitespace-nowrap"
              >
                My Team
              </motion.span>
            )}
          </Link>
        )}
        {!isCoProducerOnly && (
          <Link
            to="/settings"
            data-guide="settings"
            className="flex h-12 items-center rounded-lg text-muted-foreground transition-colors hover:bg-muted/10 hover:text-foreground"
            aria-label="Settings"
          >
            <div className="flex w-12 flex-shrink-0 items-center justify-center">
              <Settings className="h-5 w-5" />
            </div>
            {expanded && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="whitespace-nowrap"
              >
                Settings
              </motion.span>
            )}
          </Link>
        )}
        <button
          onClick={handleLogout}
          data-guide="logout"
          className="flex h-12 items-center rounded-lg text-muted-foreground transition-colors hover:bg-muted/10 hover:text-foreground"
          aria-label="Logout"
        >
          <div className="flex w-12 flex-shrink-0 items-center justify-center">
            <LogOut className="h-5 w-5" />
          </div>
          {expanded && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="whitespace-nowrap"
            >
              Logout
            </motion.span>
          )}
        </button>
      </div>
    </motion.aside>
  );
};

export default AppSidebar;
